import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import {
  CopyModalContainer,
  CopyModalContent,
  CopyModalContentTitle,
  CopyModalIncludeContainer,
  CopyModalIncludeItem,
  CopyModalIncludeItemDescription,
  CopyModalIncludeItemText,
  CopyModalIncludeItemTitle,
  CopyModalIncludeRow,
  CopyModalMainContent,
  CopyModalTextInputContainer,
  ProjectTitleContainer,
  StaticBannerContainer
} from '../styled-components'
import TextInputComponent from '../../stories/molecules/text-input'
import RecursiveFolderList from '../../recursive-folder-list'
import CheckboxComponent from '../../stories/atoms/checkboxes'
import {
  checkboxSizeOptions,
  checkboxVariantOptions
} from '../../stories/atoms/checkboxes/constants'
import { Confirm, Substract } from '../../stories/atoms/icons/essentials'
import ButtonComponent from '../../stories/atoms/button'
import {
  buttonSizeOptions,
  buttonVariantOptions
} from '../../stories/atoms/button/constants'
import { iconSizeOptions } from '../../stories/atoms/icons/icons-sizes'
import { copyProjectMembersTabOptions } from '../constants'
import { textInputHintVariantOptions } from '../../stories/molecules/text-input/constants'
import StaticBannerComponent from '../../stories/molecules/static-banner'
import { staticBannerVariantOptions } from '../../stories/molecules/static-banner/constants'

const CopyProjectMainContent = ({
  sourceProjectName = '',
  defaultFolderId = null,
  value = {},
  summary = {},
  checkboxState = {},
  hasSelectableItemsByTab = {},
  teamGroupsIncludeMembers = false,
  actions = {},
  isLoading = false,
  titleInputHint = null,
  projectCreationError = null
}) => {
  const { formatMessage } = useIntl()
  const titleInputRef = useRef(null)

  useEffect(() => {
    if (!isLoading && titleInputRef.current) {
      titleInputRef.current.focus()
    }
  }, [isLoading])

  const renderCheckboxIndicator = (checked) => (
    <CheckboxComponent.Indicator>
      {checked === 'indeterminate' && (
        <Substract size={iconSizeOptions['ICON-S']} />
      )}
      {checked === true && <Confirm size={iconSizeOptions['ICON-S']} />}
    </CheckboxComponent.Indicator>
  )

  const includeItemsLeft = [
    {
      id: 'categories',
      title: formatMessage({ id: 'projectSettings_pinCategories' }),
      disabled: value.include?.fields
    },
    {
      id: 'fields',
      title: formatMessage({ id: 'pin_fields_and_pin_designs' })
    },
    {
      id: 'datasets',
      title: formatMessage({ id: 'datasets_heading' })
    },
    {
      id: 'templates',
      title: formatMessage({ id: 'reports_templates' })
    }
  ]

  const includeItemsRight = [
    {
      id: copyProjectMembersTabOptions.MEMBERS,
      title: formatMessage({ id: 'members' }),
      description: summary?.[copyProjectMembersTabOptions.MEMBERS],
      checked: checkboxState?.[copyProjectMembersTabOptions.MEMBERS],
      disabled:
        !hasSelectableItemsByTab?.[copyProjectMembersTabOptions.MEMBERS],
      onCheckedChange: (nextValue) =>
        actions.toggleSelectionCheckbox?.(
          copyProjectMembersTabOptions.MEMBERS,
          nextValue
        ),
      actionLabel: formatMessage({ id: 'select' }),
      onAction: () =>
        actions.openMembersSelector(copyProjectMembersTabOptions.MEMBERS)
    },
    {
      id: copyProjectMembersTabOptions.CONTACTS,
      title: formatMessage({ id: 'personas_contacts' }),
      description: summary?.[copyProjectMembersTabOptions.CONTACTS],
      checked: checkboxState?.[copyProjectMembersTabOptions.CONTACTS],
      disabled:
        !hasSelectableItemsByTab?.[copyProjectMembersTabOptions.CONTACTS],
      onCheckedChange: (nextValue) =>
        actions.toggleSelectionCheckbox?.(
          copyProjectMembersTabOptions.CONTACTS,
          nextValue
        ),
      actionLabel: formatMessage({ id: 'select' }),
      onAction: () =>
        actions.openMembersSelector(copyProjectMembersTabOptions.CONTACTS)
    },
    {
      id: copyProjectMembersTabOptions.TEAMS,
      title: formatMessage({ id: 'teams_titleTeamGroups' }),
      description: summary?.[copyProjectMembersTabOptions.TEAMS],
      checked: checkboxState?.[copyProjectMembersTabOptions.TEAMS],
      disabled: !hasSelectableItemsByTab?.[copyProjectMembersTabOptions.TEAMS],
      onCheckedChange: (nextValue) =>
        actions.toggleSelectionCheckbox?.(
          copyProjectMembersTabOptions.TEAMS,
          nextValue
        ),
      actionLabel: formatMessage({ id: 'select' }),
      onAction: () =>
        actions.openMembersSelector(copyProjectMembersTabOptions.TEAMS)
    },
    {
      id: 'teams_members',
      hide: !checkboxState[copyProjectMembersTabOptions.TEAMS],
      title: formatMessage({ id: 'include_team_members' }),
      checked: teamGroupsIncludeMembers,
      onCheckedChange: actions.toggleIncludeTeamGroupMembers
    }
  ]

  const handleIncludeChange = (id) => (nextValue) => {
    actions.toggleInclude?.(id, nextValue)
  }

  const handleChangeTitle = (event) => {
    actions.setName?.(event.target.value)
  }

  return (
    <CopyModalContainer>
      <StaticBannerContainer>
        {projectCreationError && (
          <StaticBannerComponent
            variant={staticBannerVariantOptions.ERROR}
            message={projectCreationError}
          />
        )}
      </StaticBannerContainer>
      <ProjectTitleContainer>
        <TextInputComponent
          value={sourceProjectName}
          readOnly
          label={formatMessage({ id: 'project' })}
        />
      </ProjectTitleContainer>
      <CopyModalContent>
        <CopyModalMainContent>
          <CopyModalTextInputContainer>
            <TextInputComponent
              ref={titleInputRef}
              label={formatMessage({ id: 'title' })}
              value={value.name}
              onChange={handleChangeTitle}
              disabled={isLoading}
              hintMessage={titleInputHint}
              hintVariant={textInputHintVariantOptions.ERROR}
              hintShow={!!titleInputHint}
            />
            <RecursiveFolderList
              onSelectItem={actions.setParentId}
              defaultFolderId={defaultFolderId}
            />
          </CopyModalTextInputContainer>
        </CopyModalMainContent>
      </CopyModalContent>

      <CopyModalContentTitle>
        {formatMessage({ id: 'include' })}
      </CopyModalContentTitle>

      <CopyModalContent>
        <CopyModalMainContent>
          <CopyModalIncludeContainer>
            <div>
              {includeItemsLeft.map((item) => (
                <CopyModalIncludeItem key={item.id}>
                  <CopyModalIncludeRow>
                    <CheckboxComponent.Root
                      size={checkboxSizeOptions.MEDIUM}
                      variant={checkboxVariantOptions.STANDARD}
                      checked={value.include?.[item.id]}
                      disabled={isLoading || item.disabled}
                      onCheckedChange={handleIncludeChange(item.id)}
                    >
                      {renderCheckboxIndicator(value.include?.[item.id])}
                    </CheckboxComponent.Root>

                    <CopyModalIncludeItemText>
                      <CopyModalIncludeItemTitle>
                        {item.title}
                      </CopyModalIncludeItemTitle>
                      {item.description && (
                        <CopyModalIncludeItemDescription>
                          {item.description}
                        </CopyModalIncludeItemDescription>
                      )}
                    </CopyModalIncludeItemText>
                  </CopyModalIncludeRow>

                  {item.actionLabel && (
                    <ButtonComponent
                      size={buttonSizeOptions.XSMALL}
                      variant={buttonVariantOptions['TERTIARY-HOLLOW']}
                      onClick={item.onAction}
                      disabled={isLoading}
                    >
                      {item.actionLabel}
                    </ButtonComponent>
                  )}
                </CopyModalIncludeItem>
              ))}
            </div>
            <div>
              {includeItemsRight.map((item) => {
                if (item.hide) return null

                return (
                  <CopyModalIncludeItem key={item.id}>
                    <CopyModalIncludeRow>
                      <CheckboxComponent.Root
                        size={checkboxSizeOptions.MEDIUM}
                        variant={checkboxVariantOptions.STANDARD}
                        checked={item.checked}
                        disabled={isLoading || item.disabled}
                        onCheckedChange={item.onCheckedChange}
                      >
                        {renderCheckboxIndicator(item.checked)}
                      </CheckboxComponent.Root>

                      <CopyModalIncludeItemText>
                        <CopyModalIncludeItemTitle>
                          {item.title}
                        </CopyModalIncludeItemTitle>
                        {item.description && (
                          <CopyModalIncludeItemDescription>
                            {item.description}
                          </CopyModalIncludeItemDescription>
                        )}
                      </CopyModalIncludeItemText>
                    </CopyModalIncludeRow>

                    {item.actionLabel && (
                      <ButtonComponent
                        size={buttonSizeOptions.XSMALL}
                        variant={buttonVariantOptions['TERTIARY-HOLLOW']}
                        onClick={item.onAction}
                        disabled={isLoading || item.disabled}
                      >
                        {item.actionLabel}
                      </ButtonComponent>
                    )}
                  </CopyModalIncludeItem>
                )
              })}
            </div>
          </CopyModalIncludeContainer>
        </CopyModalMainContent>
      </CopyModalContent>
    </CopyModalContainer>
  )
}

CopyProjectMainContent.propTypes = {
  sourceProjectName: PropTypes.string,
  defaultFolderId: PropTypes.string,
  value: PropTypes.shape({
    name: PropTypes.string,
    parentId: PropTypes.string,
    include: PropTypes.shape({
      categories: PropTypes.bool,
      fields: PropTypes.bool,
      datasets: PropTypes.bool,
      templates: PropTypes.bool
    })
  }),
  summary: PropTypes.shape({
    members: PropTypes.string,
    teams: PropTypes.string,
    contacts: PropTypes.string
  }),
  checkboxState: PropTypes.shape({
    members: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.oneOf(['indeterminate'])
    ]),
    teams: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.oneOf(['indeterminate'])
    ]),
    contacts: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.oneOf(['indeterminate'])
    ])
  }),
  hasSelectableItemsByTab: PropTypes.shape({
    members: PropTypes.bool,
    teams: PropTypes.bool,
    contacts: PropTypes.bool
  }),
  teamGroupsIncludeMembers: PropTypes.bool,
  actions: PropTypes.shape({
    setName: PropTypes.func,
    setParentId: PropTypes.func,
    toggleInclude: PropTypes.func,
    toggleSelectionCheckbox: PropTypes.func,
    toggleIncludeTeamGroupMembers: PropTypes.func,
    openMembersSelector: PropTypes.func
  }),
  isLoading: PropTypes.bool
}

export default CopyProjectMainContent
